import { company } from '@/content/company';
import type { FaqEntry } from '@/content/faq';
import { appPublicName, appTiers, type PublicApp } from '@/content/apps';
import { effectivePlanPriceCents, isCustomQuotePlan, type PublicPlan } from '@/content/plans';

/**
 * Dados estruturados (schema.org, JSON-LD).
 *
 * É daqui que o Google monta o painel da marca e que os assistentes de IA tiram
 * respostas objetivas: quem é a empresa, o que o produto faz e quanto custa.
 * Tudo sai de dado real (empresa, catálogo, FAQ) e vai no HTML gerado no build,
 * porque JSON-LD injetado por JavaScript não é lido por quem não roda JS.
 */
export const SITE_URL = 'https://operax.digital';
export const ORG_ID = `${SITE_URL}/#organizacao`;
export const WEBSITE_ID = `${SITE_URL}/#site`;
export const PRODUCT_ID = `${SITE_URL}/#produto`;

export type JsonLd = Record<string, unknown>;

export function absoluteUrl(path: string): string {
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

function reais(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function organizationLd(): JsonLd {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: company.productName,
    legalName: company.legalName,
    alternateName: 'Operax, plataforma de atendimento no WhatsApp',
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/operax-logo.png`,
    description:
      'Plataforma brasileira de atendimento e vendas no WhatsApp oficial da Meta, com CRM, funil e apps como agenda, estoque, Pix e nota fiscal.',
    taxID: company.cnpj,
    email: company.email,
    telephone: `+${company.phoneE164Digits}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: company.street,
      addressLocality: company.city,
      addressRegion: company.state,
      postalCode: company.zipCode,
      addressCountry: 'BR',
    },
    areaServed: { '@type': 'Country', name: 'Brasil' },
    knowsLanguage: 'pt-BR',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      telephone: `+${company.phoneE164Digits}`,
      email: company.email,
      areaServed: 'BR',
      availableLanguage: 'Portuguese',
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    },
  };
}

export function websiteLd(): JsonLd {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: `${SITE_URL}/`,
    name: 'Operax',
    inLanguage: 'pt-BR',
    publisher: { '@id': ORG_ID },
  };
}

/** Planos compráveis online viram `Offer`; Enterprise sob consulta fica de fora do preço. */
function planOffers(plans: PublicPlan[]): JsonLd[] {
  return plans
    .filter((plan) => !isCustomQuotePlan(plan) && effectivePlanPriceCents(plan) > 0)
    .map((plan) => ({
      '@type': 'Offer',
      name: `Plano ${plan.name}`,
      price: reais(effectivePlanPriceCents(plan)),
      priceCurrency: 'BRL',
      url: `${SITE_URL}/precos`,
      availability: 'https://schema.org/InStock',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: reais(effectivePlanPriceCents(plan)),
        priceCurrency: 'BRL',
        billingDuration: 'P1M',
        unitText: 'mês',
      },
    }));
}

export function productLd(plans: PublicPlan[]): JsonLd {
  const offers = planOffers(plans);
  const prices = offers.map((o) => Number(o.price));
  return {
    '@type': 'SoftwareApplication',
    '@id': PRODUCT_ID,
    name: 'Operax',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'CRM e atendimento no WhatsApp',
    operatingSystem: 'Web',
    url: `${SITE_URL}/produto`,
    inLanguage: 'pt-BR',
    description:
      'Inbox de atendimento no WhatsApp oficial da Meta com filas, histórico e funil de vendas em cada conversa. Agenda, estoque, Pix, NFS-e, PDV e outros módulos são ativados pelo Portal de Apps.',
    featureList: [
      'Atendimento no WhatsApp pela API oficial da Meta',
      'Filas, distribuição e histórico por contato',
      'Funil de vendas ligado à conversa',
      'Portal de Apps com agenda, estoque, Pix, NFS-e, PDV e contratos',
      'Hub Comercial: pedidos de marketplaces e estoque único',
      'CoreMed: recepção, fila, prontuário e guias para clínicas',
      'IA que responde no WhatsApp com base de conhecimento da empresa',
      'Cobrança por Pix e emissão de nota fiscal a partir do atendimento',
    ],
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Clínicas, oficinas, varejo, restaurantes, associações, cursos e vendedores de marketplace',
    },
    publisher: { '@id': ORG_ID },
    offers:
      prices.length > 0
        ? {
            '@type': 'AggregateOffer',
            priceCurrency: 'BRL',
            lowPrice: Math.min(...prices).toFixed(2),
            highPrice: Math.max(...prices).toFixed(2),
            offerCount: offers.length,
            offers,
          }
        : undefined,
  };
}

export function appLd(app: PublicApp, path: string): JsonLd {
  const cents = app.pricing?.monthly_price_cents ?? 0;
  const soon = app.tier === 'soon' || app.availability === 'coming_soon';
  return {
    '@type': 'SoftwareApplication',
    '@id': `${absoluteUrl(path)}#app`,
    name: appPublicName(app).startsWith('Operax') ? appPublicName(app) : `${appPublicName(app)} (app da Operax)`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: absoluteUrl(path),
    description: app.description ?? undefined,
    isPartOf: { '@id': PRODUCT_ID },
    publisher: { '@id': ORG_ID },
    // App incluso não tem preço próprio: vem dentro de um plano pago. Offer com
    // preço 0 diria ao Google que é grátis.
    offers: soon || app.tier === 'included'
      ? undefined
      : appTiers(app).length > 0
        ? // Um Offer por nível: o preço de «a partir de» sozinho esconderia o nível que a maioria contrata.
          appTiers(app).map((tier) => ({
            '@type': 'Offer',
            name: tier.name,
            price: reais(tier.monthly_price_cents),
            priceCurrency: 'BRL',
            availability: 'https://schema.org/InStock',
            description: `Nível ${tier.name}, mensal`,
            url: absoluteUrl(path),
          }))
        : {
            '@type': 'Offer',
            price: reais(cents),
            priceCurrency: 'BRL',
            availability: 'https://schema.org/InStock',
            description: 'Add-on mensal',
            url: absoluteUrl(path),
          },
  };
}

export function faqLd(entries: FaqEntry[], path: string): JsonLd {
  return {
    '@type': 'FAQPage',
    '@id': `${absoluteUrl(path)}#faq`,
    mainEntity: entries.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: { '@type': 'Answer', text: entry.answer },
    })),
  };
}

export function breadcrumbLd(items: { name: string; path: string }[]): JsonLd {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function articleLd(params: {
  path: string;
  headline: string;
  description: string;
  published: string;
  updated: string;
}): JsonLd {
  return {
    '@type': 'Article',
    '@id': `${absoluteUrl(params.path)}#artigo`,
    headline: params.headline,
    description: params.description,
    inLanguage: 'pt-BR',
    datePublished: params.published,
    dateModified: params.updated,
    mainEntityOfPage: absoluteUrl(params.path),
    author: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    image: `${SITE_URL}/og-site.png`,
  };
}

export function webPageLd(path: string, name: string, description: string): JsonLd {
  return {
    '@type': 'WebPage',
    '@id': `${absoluteUrl(path)}#pagina`,
    url: absoluteUrl(path),
    name,
    description,
    inLanguage: 'pt-BR',
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORG_ID },
  };
}

/** Junta os nós num único `@graph`, que é como o Google recomenda ligar entidades por `@id`. */
export function graph(nodes: (JsonLd | undefined)[]): JsonLd {
  return { '@context': 'https://schema.org', '@graph': nodes.filter(Boolean) };
}
